import { useEffect, useState } from "react";
import { Check, X } from "lucide-react";
import { ENABLE_I18N, LANGUAGES, useI18n } from "@/lib/i18n";

/**
 * One-shot confirmation toast that appears on the very first visit
 * when the site auto-applied a non-default browser language.
 *
 * It tells the visitor "we noticed you speak X — here's the site in X.
 * Not right? Switch to DE / FR / IT / EN with one click."
 *
 * Auto-dismisses after 6s. Localized in DE / FR / IT / EN.
 * Never shows again on the same browser.
 */
const COPY = {
  de: {
    overline: "Sprache erkannt",
    headline: (l) => `Diese Seite wird Ihnen auf ${l} angezeigt.`,
    other: "Andere Sprache:",
    name: "Deutsch",
    aria_close: "Hinweis schliessen",
  },
  fr: {
    overline: "Langue détectée",
    headline: (l) => `Cette page vous est affichée en ${l}.`,
    other: "Autre langue :",
    name: "Français",
    aria_close: "Fermer la notification",
  },
  it: {
    overline: "Lingua rilevata",
    headline: (l) => `Questa pagina viene mostrata in ${l}.`,
    other: "Altra lingua:",
    name: "Italiano",
    aria_close: "Chiudi avviso",
  },
  en: {
    overline: "Language detected",
    headline: (l) => `This page is displayed in ${l}.`,
    other: "Other language:",
    name: "English",
    aria_close: "Dismiss notification",
  },
};

const FlagMini = ({ code }) => {
  const cls = "block w-full h-full";
  if (code === "de")
    return (
      <svg viewBox="0 0 5 3" className={cls} preserveAspectRatio="none" aria-hidden="true">
        <rect width="5" height="1" fill="#000" />
        <rect y="1" width="5" height="1" fill="#D00" />
        <rect y="2" width="5" height="1" fill="#FFCE00" />
      </svg>
    );
  if (code === "fr")
    return (
      <svg viewBox="0 0 3 2" className={cls} preserveAspectRatio="none" aria-hidden="true">
        <rect width="1" height="2" fill="#002654" />
        <rect x="1" width="1" height="2" fill="#fff" />
        <rect x="2" width="1" height="2" fill="#ED2939" />
      </svg>
    );
  if (code === "it")
    return (
      <svg viewBox="0 0 3 2" className={cls} preserveAspectRatio="none" aria-hidden="true">
        <rect width="1" height="2" fill="#009246" />
        <rect x="1" width="1" height="2" fill="#fff" />
        <rect x="2" width="1" height="2" fill="#CE2B37" />
      </svg>
    );
  if (code === "en")
    return (
      <svg viewBox="0 0 60 30" className={cls} preserveAspectRatio="none" aria-hidden="true">
        <clipPath id="fk-toast-uk">
          <path d="M30,15 h30 v15 z v15 h-30 z h-30 v-15 z v-15 h30 z" />
        </clipPath>
        <rect width="60" height="30" fill="#012169" />
        <path d="M0,0 L60,30 M60,0 L0,30" stroke="#fff" strokeWidth="6" />
        <path d="M0,0 L60,30 M60,0 L0,30" clipPath="url(#fk-toast-uk)" stroke="#C8102E" strokeWidth="4" />
        <path d="M30,0 v30 M0,15 h60" stroke="#fff" strokeWidth="10" />
        <path d="M30,0 v30 M0,15 h60" stroke="#C8102E" strokeWidth="6" />
      </svg>
    );
  return null;
};

export default function LanguageDetectedToast() {
  const { lang, setLang, autoDetected, clearAutoDetected } = useI18n();
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (!ENABLE_I18N) return;
    if (!autoDetected) return;
    // Small delay so the splash screen finishes first
    const showTimer = setTimeout(() => setVisible(true), 1400);
    const hideTimer = setTimeout(() => {
      setVisible(false);
      // give it time to fade, then clear
      setTimeout(clearAutoDetected, 500);
    }, 7400);
    return () => {
      clearTimeout(showTimer);
      clearTimeout(hideTimer);
    };
  }, [autoDetected, clearAutoDetected]);

  if (!ENABLE_I18N || !autoDetected) return null;

  const c = COPY[lang] || COPY.de;
  const otherLanguages = LANGUAGES.filter((l) => l.code !== lang);

  const dismiss = () => {
    setVisible(false);
    setTimeout(clearAutoDetected, 350);
  };

  const switchTo = (code) => {
    setLang(code);
    dismiss();
  };

  return (
    <div
      data-testid="lang-detected-toast"
      role="status"
      aria-live="polite"
      className={`fixed z-[55] top-24 right-6 max-w-[380px] transition-all duration-500 ${
        visible ? "opacity-100 translate-y-0" : "opacity-0 -translate-y-2 pointer-events-none"
      }`}
    >
      <div className="relative bg-[#0E0E10]/95 backdrop-blur-md border border-white/15 shadow-[0_24px_60px_-20px_rgba(0,0,0,0.7)] p-4 pr-9">
        <button
          type="button"
          onClick={dismiss}
          data-testid="lang-detected-close"
          aria-label={c.aria_close}
          className="absolute top-2 right-2 p-1 text-zinc-500 hover:text-white transition-colors"
        >
          <X className="w-3.5 h-3.5" strokeWidth={1.5} />
        </button>

        <div className="flex items-start gap-3">
          <span className="block flex-none w-8 h-[22px] mt-0.5 ring-1 ring-white/20 overflow-hidden">
            <FlagMini code={lang} />
          </span>
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.2em] text-emerald-400 mb-1.5">
              <Check className="w-3 h-3" strokeWidth={2.2} />
              {c.overline}
            </div>
            <p className="text-sm text-zinc-200 leading-snug mb-3">
              {c.headline(c.name)}
            </p>
            <div className="flex items-center gap-1 flex-wrap">
              <span className="text-[11px] uppercase tracking-[0.16em] text-zinc-500 mr-1">
                {c.other}
              </span>
              {otherLanguages.map((l) => (
                <button
                  key={l.code}
                  type="button"
                  onClick={() => switchTo(l.code)}
                  data-testid={`lang-detected-switch-${l.code}`}
                  className="group inline-flex items-center gap-1.5 px-2 py-1 border border-white/15 text-zinc-400 hover:text-white hover:border-white/40 hover:bg-white/[0.04] transition-all"
                  title={l.name}
                >
                  <span className="block w-[14px] h-[10px] overflow-hidden ring-1 ring-white/20">
                    <FlagMini code={l.code} />
                  </span>
                  <span className="text-[11px] font-semibold tracking-[0.16em] uppercase">
                    {l.label}
                  </span>
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* progress bar that drains over 6s, indicating auto-dismiss */}
        <div className="absolute bottom-0 left-0 right-0 h-px bg-white/5">
          <div
            className={`h-full bg-emerald-400/60 transition-all ease-linear ${
              visible ? "w-0" : "w-full"
            }`}
            style={{ transitionDuration: visible ? "6000ms" : "0ms" }}
          />
        </div>
      </div>
    </div>
  );
}
